import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, findCurrency, fmtMoney } from "@/lib/api";
import { streamProposal } from "@/lib/stream";
import { COMPARE } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  Loader2,
  Download,
  FileType2,
  Plus,
  X,
  Building2,
  Share2,
  Copy,
} from "lucide-react";
import ComparisonGrid from "@/components/ComparisonGrid";
import ShareAnalytics from "@/components/ShareAnalytics";
import { buildActionableSummary } from "@/lib/actionable";

const DEFAULT_REGIONS = ["north_america", "eu", "west_africa"];

const SHARE_TOKENS_KEY = "imali:share-tokens";
const loadShareTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(SHARE_TOKENS_KEY) || "{}");
  } catch {
    return {};
  }
};
const saveShareToken = (id, token) => {
  const map = loadShareTokens();
  map[id] = token;
  localStorage.setItem(SHARE_TOKENS_KEY, JSON.stringify(map));
};
const getShareToken = (id) => loadShareTokens()[id] || null;

export default function CompareBankingSystems() {
  const [industries, setIndustries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [regions, setRegions] = useState([]);

  const [form, setForm] = useState({
    business_name: "Acme Ventures",
    industry_id: "commercial",
    loan_amount: 250000,
    currency_code: "USD",
    tenure_months: 36,
    annual_revenue: 1500000,
    purpose: "Working capital & inventory expansion",
  });
  const [selectedRegions, setSelectedRegions] = useState(DEFAULT_REGIONS);
  const [suggestions, setSuggestions] = useState([]); // ranked regions for current industry
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const [advisor, setAdvisor] = useState("");
  const [advisorStreaming, setAdvisorStreaming] = useState(false);
  const [shareId, setShareId] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      const [iRes, cRes, rRes] = await Promise.all([
        api.get("/industries"),
        api.get("/currencies"),
        api.get("/regions"),
      ]);
      setIndustries(iRes.data.industries);
      setCurrencies(cRes.data.currencies);
      setRegions(rRes.data.regions);
    })();
  }, []);

  // Fetch industry-specific region ranking whenever the industry changes.
  useEffect(() => {
    if (!form.industry_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/regions-by-industry?industry_id=${form.industry_id}`
        );
        if (!cancelled) setSuggestions(data.regions || []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.industry_id]);

  const topSuggestions = useMemo(
    () => suggestions.filter((s) => s.score > 0).slice(0, 4),
    [suggestions]
  );

  const applyTopSuggestions = (n = 3) => {
    const picks = topSuggestions.slice(0, n).map((s) => s.region_id);
    if (!picks.length) return;
    // Non-destructive: append missing picks, keep existing order, cap at 4.
    const merged = [...selectedRegions];
    for (const id of picks) {
      if (merged.includes(id)) continue;
      if (merged.length >= 4) {
        // Replace the last unmatched slot instead of dropping the suggestion.
        merged[merged.length - 1] = id;
      } else {
        merged.push(id);
      }
    }
    setSelectedRegions(Array.from(new Set(merged)).slice(0, 4));
    toast.success(
      `Suggested ${picks.length} region${picks.length > 1 ? "s" : ""} for ${
        industries.find((i) => i.id === form.industry_id)?.name || "this industry"
      }.`
    );
  };

  const currency = useMemo(
    () => findCurrency(currencies, form.currency_code),
    [currencies, form.currency_code]
  );

  const patch = (obj) => setForm((s) => ({ ...s, ...obj }));

  const addRegion = () => {
    if (selectedRegions.length >= 4) return;
    const next = regions.find(
      (r) => !selectedRegions.includes(r.id)
    );
    if (next) setSelectedRegions([...selectedRegions, next.id]);
  };
  const removeRegion = (idx) => {
    if (selectedRegions.length <= 2) return;
    setSelectedRegions(selectedRegions.filter((_, i) => i !== idx));
  };
  const setRegionAt = (idx, id) => {
    const copy = [...selectedRegions];
    copy[idx] = id;
    setSelectedRegions(copy);
  };

  const runCompare = async () => {
    // dedupe & validate
    const uniq = Array.from(new Set(selectedRegions));
    if (uniq.length < 2) {
      toast.error("Please pick at least 2 distinct regions.");
      return;
    }
    setRunning(true);
    setResult(null);
    setAdvisor("");
    setShareId("");
    try {
      const payload = {
        ...form,
        loan_amount: Number(form.loan_amount),
        annual_revenue: Number(form.annual_revenue),
        tenure_months: Number(form.tenure_months),
        region_ids: uniq,
      };
      const { data } = await api.post("/compare/loan", payload);
      setResult(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Compare failed";
      toast.error(String(msg));
    } finally {
      setRunning(false);
    }
  };

  const streamAdvisor = async () => {
    if (!result) return;
    setAdvisor("");
    setAdvisorStreaming(true);
    const payload = {
      ...form,
      loan_amount: Number(form.loan_amount),
      annual_revenue: Number(form.annual_revenue),
      tenure_months: Number(form.tenure_months),
      region_ids: result.columns.map((c) => c.region_id),
    };
    try {
      await streamProposal(
        "/compare/loan/advisor/stream",
        payload,
        {
          onToken: (chunk) => setAdvisor((prev) => prev + chunk),
          onDone: (full) => setAdvisor(full),
        }
      );
    } catch (err) {
      toast.error(err?.message || "Advisor stream failed");
    } finally {
      setAdvisorStreaming(false);
    }
  };

  const downloadExport = async (format) => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/export/comparison/${format}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ result, advisor_takeaway: advisor || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Imali_Comparison_${result.business_name.replace(/\W+/g, "_")}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch {
      toast.error(`Unable to download ${format.toUpperCase()}`);
    }
  };

  const shareUrl = shareId
    ? `${window.location.origin}/api/share/${shareId}`
    : "";

  const createShareLink = async () => {
    if (!result) return;
    setSharing(true);
    try {
      const { data } = await api.post("/compare/loan/save", {
        result,
        advisor_takeaway: advisor || null,
        input_purpose: form.purpose,
        input_annual_revenue: Number(form.annual_revenue) || null,
      });
      setShareId(data.id);
      if (data.owner_token) saveShareToken(data.id, data.owner_token);
      const url = `${window.location.origin}/api/share/${data.id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied — paste it in Slack, LinkedIn, X, WhatsApp…");
      } catch {
        toast.success("Share link created — copy it from the field below");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to create share link");
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Copied");
    } catch {
      toast.error("Unable to copy — select and copy manually");
    }
  };

  return (
    <div
      data-testid={COMPARE.page}
      className="max-w-7xl mx-auto px-6 lg:px-10 py-14"
    >
      <div className="max-w-3xl">
        <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold mb-4">
          Compare banking systems
        </p>
        <h1 className="font-display text-4xl lg:text-6xl leading-none tracking-tighter font-light">
          Same borrower.{" "}
          <span className="text-primary">Two to four banking regions.</span>{" "}
          <span className="italic font-normal">One view.</span>
        </h1>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
          Enter the applicant once and compare how a facility would look under
          different regulatory frameworks — rate ranges, payment rails,
          documents and guarantee schemes — side by side. Add an AI advisor
          takeaway on demand.
        </p>
      </div>

      {/* Form */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-6">
          <div className="flex items-baseline gap-4 border-b border-border pb-3">
            <span className="font-mono text-xs tracking-[0.28em] text-secondary">01</span>
            <h2 className="font-display text-2xl tracking-tight">Borrower profile</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Business name">
              <Input
                data-testid={COMPARE.businessName}
                value={form.business_name}
                onChange={(e) => patch({ business_name: e.target.value })}
              />
            </Field>
            <Field label="Industry">
              <Select
                value={form.industry_id}
                onValueChange={(v) => patch({ industry_id: v })}
              >
                <SelectTrigger data-testid={COMPARE.industry}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Loan amount">
              <Input
                data-testid={COMPARE.amount}
                type="number"
                value={form.loan_amount}
                onChange={(e) => patch({ loan_amount: e.target.value })}
              />
            </Field>
            <Field label="Currency">
              <Select
                value={form.currency_code}
                onValueChange={(v) => patch({ currency_code: v })}
              >
                <SelectTrigger data-testid={COMPARE.currency}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="mr-2">{c.flag}</span>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tenure (months)">
              <Input
                data-testid={COMPARE.tenure}
                type="number"
                value={form.tenure_months}
                onChange={(e) => patch({ tenure_months: e.target.value })}
              />
            </Field>
            <Field label="Annual revenue">
              <Input
                data-testid={COMPARE.revenue}
                type="number"
                value={form.annual_revenue}
                onChange={(e) => patch({ annual_revenue: e.target.value })}
              />
            </Field>
            <Field label="Purpose" full>
              <Textarea
                data-testid={COMPARE.purpose}
                rows={2}
                value={form.purpose}
                onChange={(e) => patch({ purpose: e.target.value })}
              />
            </Field>
          </div>
        </div>

        {/* Region selection */}
        <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
          <div className="flex items-baseline gap-4 border-b border-border pb-3">
            <span className="font-mono text-xs tracking-[0.28em] text-secondary">02</span>
            <h2 className="font-display text-2xl tracking-tight">
              Regions ({selectedRegions.length}/4)
            </h2>
          </div>

          {/* Prescriptive matchmaker — surfaces the strongest regions for the
              chosen industry using the /api/regions-by-industry ranking. */}
          {topSuggestions.length > 0 && (
            <div
              data-testid={COMPARE.suggestBar}
              className="mt-4 rounded-xl border border-dashed border-secondary/40 bg-secondary/[0.06] p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-[10px] tracking-[0.22em] uppercase font-semibold text-secondary">
                  Suggested for{" "}
                  <span className="text-foreground">
                    {industries.find((i) => i.id === form.industry_id)?.name ||
                      "this industry"}
                  </span>
                </p>
                <Button
                  data-testid={COMPARE.suggestApply}
                  size="sm"
                  variant="outline"
                  className="rounded-full h-7 text-xs"
                  onClick={() => applyTopSuggestions(3)}
                >
                  <Sparkles aria-hidden="true" className="w-3 h-3 mr-1.5" />
                  Use top 3
                </Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {topSuggestions.map((s) => {
                  const active = selectedRegions.includes(s.region_id);
                  return (
                    <button
                      key={s.region_id}
                      type="button"
                      data-testid={COMPARE.suggestChip(s.region_id)}
                      onClick={() => {
                        if (active) return;
                        if (selectedRegions.length >= 4) {
                          setSelectedRegions([
                            ...selectedRegions.slice(0, 3),
                            s.region_id,
                          ]);
                        } else {
                          setSelectedRegions([...selectedRegions, s.region_id]);
                        }
                      }}
                      title={
                        s.matches.length
                          ? `Matched: ${s.matches.slice(0, 3).join(" · ")}${
                              s.matches.length > 3 ? " …" : ""
                            }`
                          : ""
                      }
                      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                        active
                          ? "bg-primary/10 border-primary/40 text-primary cursor-default"
                          : "bg-card border-border text-foreground hover:border-secondary/60 hover:bg-secondary/10"
                      }`}
                    >
                      <span className="font-mono text-secondary">{s.score}×</span>
                      <span className="font-semibold">{s.region_short}</span>
                      <span className="text-muted-foreground truncate max-w-[220px]">
                        {s.matches[0] || s.region_name}
                        {s.matches.length > 1 && (
                          <span className="ml-1 text-[10px]">
                            +{s.matches.length - 1} more
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-4 space-y-3">
            {selectedRegions.map((id, i) => (
              <div key={`slot-${i}-${id}`} className="flex items-center gap-2">
                <Select
                  value={id}
                  onValueChange={(v) => setRegionAt(i, v)}
                >
                  <SelectTrigger
                    className="flex-1"
                    data-testid={COMPARE.regionSelect(i)}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedRegions.length > 2 && (
                  <Button
                    data-testid={COMPARE.removeRegion(i)}
                    size="icon"
                    variant="ghost"
                    onClick={() => removeRegion(i)}
                    className="rounded-full text-muted-foreground hover:text-destructive"
                  >
                    <X aria-hidden="true" className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {selectedRegions.length < 4 && (
              <Button
                data-testid={COMPARE.addRegion}
                variant="outline"
                onClick={addRegion}
                className="w-full rounded-full"
              >
                <Plus aria-hidden="true" className="w-4 h-4 mr-2" /> Add region
              </Button>
            )}
          </div>
          <Button
            data-testid={COMPARE.runBtn}
            onClick={runCompare}
            disabled={running}
            size="lg"
            className="mt-6 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12"
          >
            {running ? (
              <>
                <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" /> Comparing…
              </>
            ) : (
              <>
                <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" /> Run comparison
              </>
            )}
          </Button>
        </div>
      </section>

      {/* Result */}
      {result && (
        <section className="mt-14 space-y-8">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <p className="text-xs tracking-[0.28em] uppercase text-secondary font-semibold">
                Comparison
              </p>
              <h2 className="mt-2 font-display text-2xl lg:text-3xl tracking-tight">
                {result.business_name} · {result.industry_name} ·{" "}
                {fmtMoney(result.loan_amount, currency)} · {result.tenure_months} mo
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                data-testid={COMPARE.shareBtn}
                variant="outline"
                size="sm"
                onClick={createShareLink}
                disabled={sharing}
                className="rounded-full"
              >
                {sharing ? (
                  <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Share2 aria-hidden="true" className="w-4 h-4 mr-2" />
                )}
                {shareId ? "Re-share" : "Share link"}
              </Button>
              <Button
                data-testid={COMPARE.downloadPdf}
                variant="outline"
                size="sm"
                onClick={() => downloadExport("pdf")}
                className="rounded-full"
              >
                <Download aria-hidden="true" className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button
                data-testid={COMPARE.downloadDocx}
                variant="outline"
                size="sm"
                onClick={() => downloadExport("docx")}
                className="rounded-full"
              >
                <FileType2 aria-hidden="true" className="w-4 h-4 mr-2" /> DOCX
              </Button>
            </div>
          </div>

          {/* Share URL display */}
          {shareId && (
            <>
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 lg:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <Share2 aria-hidden="true" className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 w-full">
                  <p className="text-[10px] tracking-[0.28em] uppercase text-primary font-semibold">
                    Public share link · social previews enabled
                  </p>
                  <input
                    data-testid={COMPARE.shareUrl}
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className="mt-1 w-full bg-transparent font-mono text-sm text-foreground outline-none"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyShare}
                  className="rounded-full"
                >
                  <Copy aria-hidden="true" className="w-4 h-4 mr-2" /> Copy
                </Button>
              </div>

              <ShareAnalytics
                shareId={shareId}
                ownerToken={getShareToken(shareId)}
                result={result}
              />
            </>
          )}

          {/* Grid */}
          {(() => {
            // Match-reasons strip — surfaces why each region in the result
            // scored for the chosen industry. Skipped when the industry has
            // zero matches across the picked regions.
            const shownRegions = result.columns.map((c) => c.region_id);
            const shownMatches = suggestions
              .filter((s) => shownRegions.includes(s.region_id) && s.matches.length)
              .sort(
                (a, b) =>
                  shownRegions.indexOf(a.region_id) -
                  shownRegions.indexOf(b.region_id)
              );
            if (!shownMatches.length) return null;
            return (
              <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
                  Why these regions for {result.industry_name}?
                </p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {shownMatches.map((s, i) => (
                    <div
                      key={s.region_id}
                      data-testid={COMPARE.whyRegion(i)}
                      className="rounded-xl border border-border bg-background/40 p-4"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-mono text-xs text-secondary">
                          {s.region_short}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {s.score} match{s.score !== 1 ? "es" : ""}
                        </span>
                      </div>
                      <ul
                        data-testid={COMPARE.whyRegionPopover(i)}
                        className="mt-2 space-y-1 text-xs text-foreground/85"
                      >
                        {s.matches.slice(0, 4).map((m) => (
                          <li key={m} className="pl-2 -indent-2 leading-snug">
                            <span className="text-secondary mr-1">·</span>
                            {m}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <ComparisonGrid
            result={result}
            advisor={advisor}
            advisorStreaming={advisorStreaming}
            mode="editable"
          />

          {/* Advisor takeaway CTA */}
          <div className="rounded-2xl border border-border bg-card p-6 lg:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[10px] tracking-[0.28em] uppercase text-secondary font-semibold">
                  On-demand
                </p>
                <h3 className="mt-1 font-display text-xl tracking-tight flex items-center gap-2">
                  <Building2 aria-hidden="true" className="w-5 h-5 text-primary" />
                  Advisor takeaway
                </h3>
                {!advisor && (
                  <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                    Click <b>Get advisor takeaway</b> to have Imali summarise
                    which region offers the fastest, cheapest and most
                    guarantee-protected path for this borrower — streamed
                    token-by-token.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {advisor && !advisorStreaming && (
                  <Button
                    data-testid={COMPARE.copyActionable}
                    variant="outline"
                    className="rounded-full"
                    onClick={async () => {
                      const summary = buildActionableSummary(result, advisor);
                      try {
                        await navigator.clipboard.writeText(summary);
                        toast.success("Actionable summary copied", {
                          description: summary.split("\n")[0],
                        });
                      } catch {
                        toast.error("Unable to copy — select the text manually");
                      }
                    }}
                  >
                    <Copy aria-hidden="true" className="w-4 h-4 mr-2" />
                    Copy actionable summary
                  </Button>
                )}
                <Button
                  data-testid={COMPARE.advisorBtn}
                  onClick={streamAdvisor}
                  disabled={advisorStreaming}
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {advisorStreaming ? (
                    <>
                      <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                      Streaming…
                    </>
                  ) : advisor ? (
                    <>
                      <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" />
                      Re-run
                    </>
                  ) : (
                    <>
                      <Sparkles aria-hidden="true" className="w-4 h-4 mr-2" />
                      Get advisor takeaway
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}


function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

